import React from "react";

// Filet de securite global : capture les erreurs de rendu React (ReferenceError,
// donnee inattendue, etc.) qui, sans lui, rendent TOUT l'ecran blanc et bloquent
// l'utilisateur. Affiche a la place un ecran d'erreur propre avec un bouton
// "Recharger". Bonus : si l'erreur vient d'un chunk JS perime (apres un deploiement,
// l'ancienne app en cache demande un chunk qui n'existe plus), on recharge une fois
// automatiquement pour recuperer la nouvelle version (evite l'ecran blanc post-deploy).
//
// Volontairement SANS dependance a l'app (pas d'import de theme/constants) : un
// ErrorBoundary doit rester fonctionnel meme si un module de l'app est en cause.

const I18N = {
  fr: { title: "Oups, un souci est survenu", body: "L'application a rencontré une erreur inattendue. Recharge la page pour continuer.", reload: "Recharger", home: "Retour à l'accueil" },
  en: { title: "Oops, something went wrong", body: "The app hit an unexpected error. Reload the page to continue.", reload: "Reload", home: "Back to home" },
  de: { title: "Hoppla, etwas ist schiefgelaufen", body: "Die App hatte einen unerwarteten Fehler. Lade die Seite neu, um fortzufahren.", reload: "Neu laden", home: "Zur Startseite" },
  it: { title: "Ops, qualcosa è andato storto", body: "L'app ha riscontrato un errore imprevisto. Ricarica la pagina per continuare.", reload: "Ricarica", home: "Torna alla home" },
  es: { title: "Vaya, algo salió mal", body: "La aplicación tuvo un error inesperado. Recarga la página para continuar.", reload: "Recargar", home: "Volver al inicio" },
  pt: { title: "Ops, algo correu mal", body: "A aplicação encontrou um erro inesperado. Recarrega a página para continuar.", reload: "Recarregar", home: "Voltar ao início" },
  nl: { title: "Oeps, er ging iets mis", body: "De applicatie liep tegen een onverwachte fout aan. Herlaad de pagina om verder te gaan.", reload: "Herladen", home: "Terug naar de startpagina" },
};

function pickLang() {
  try {
    const l = localStorage.getItem("hc_lang");
    if (l && I18N[l]) return l;
  } catch (e) {}
  try {
    const nav = (navigator.language || "fr").slice(0, 2);
    if (I18N[nav]) return nav;
  } catch (e) {}
  return "fr";
}

function isChunkLoadError(error) {
  const msg = (error && (error.message || error.name || "")) + "";
  return /ChunkLoadError/i.test(msg) || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) || /import\(\)/.test(msg);
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Chunk perime (deploiement) : on tente UN rechargement auto pour recuperer
    // la nouvelle version. Garde anti-boucle via sessionStorage.
    try {
      if (isChunkLoadError(error) && sessionStorage.getItem("hc_chunk_reloaded") !== "1") {
        sessionStorage.setItem("hc_chunk_reloaded", "1");
        window.location.reload();
        return;
      }
    } catch (e) {}
    // eslint-disable-next-line no-console
    console.error("App error caught by ErrorBoundary:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const t = I18N[pickLang()] || I18N.fr;
    const btnBase = {
      display: "block", width: "100%", maxWidth: 300, margin: "0 auto",
      padding: "14px 24px", borderRadius: 14, fontSize: 15, fontWeight: 800,
      cursor: "pointer", border: "none", fontFamily: "inherit", letterSpacing: 0.3,
    };
    return (
      <div style={{
        minHeight: "100vh", width: "100%",
        background: "radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 30%, #14132E 60%, #08081A 100%)",
        color: "#fff", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center",
        padding: "32px 24px", boxSizing: "border-box",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "rgba(200,0,26,.15)", border: "2px solid rgba(200,0,26,.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 36, marginBottom: 24, color: "#FF5A6E", fontWeight: 900,
        }}>!</div>
        <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 12, letterSpacing: -0.3 }}>{t.title}</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.6, maxWidth: 340, marginBottom: 32 }}>{t.body}</div>
        <button
          onClick={() => { try { sessionStorage.removeItem("hc_chunk_reloaded"); } catch (e) {} window.location.reload(); }}
          style={{ ...btnBase, background: "#C8001A", color: "#fff", boxShadow: "0 8px 22px rgba(200,0,26,.45)", marginBottom: 12 }}
        >{t.reload}</button>
        <button
          onClick={() => { try { window.location.href = "/"; } catch (e) { window.location.reload(); } }}
          style={{ ...btnBase, background: "rgba(255,255,255,.08)", color: "#fff", border: "1px solid rgba(255,255,255,.18)" }}
        >{t.home}</button>
      </div>
    );
  }
}
